import { Link, Redirect, Switch, useLocation } from 'react-router-dom'
import { Button, Text } from '@mantine/core'
import * as Sentry from '@sentry/react'

import { useReporter } from './providers/UserProvider'

import { MissingReporterSettingsModal } from '@/components/MissingReporterSettingsModal'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { Wall } from '@/components/Wall'
import { useAuth } from '@/hooks/useAuth'
import {
  CALENDAR_PARAMS_PATH,
  toCalendarParamsPath,
} from '@/queries/useCalendarParams'
import { Event as AdminCourtReporterEvent } from '@/routes/Admin/CourtReporterEvent/Event'
import { Dashboard as AdminDashboard } from '@/routes/Admin/Dashboard/Dashboard'
import { Event as AdminEvent } from '@/routes/Admin/Event/Event'
import { NewScript as AdminNewScript } from '@/routes/Admin/Event/NewScript/NewScript'
import { NewScriptStateEditor } from '@/routes/Admin/Event/NewScript/NewScriptStateEditor'
import { Script as AdminScript } from '@/routes/Admin/Event/Script/Script'
import { Reporters } from '@/routes/Admin/Reporters/Reporters'
import { Availability } from '@/routes/Availability'
import { Dashboard as CourtReporterDashboard } from '@/routes/CourtReporter/Dashboard/Dashboard'
import { Event as CourtReporterEvent } from '@/routes/CourtReporter/Event/Event'
import { Dashboard } from '@/routes/Dashboard/Dashboard'
import { Event } from '@/routes/Event/Event'
import { NewScript } from '@/routes/Event/NewScript/NewScript'
import { Script } from '@/routes/Event/Script/Script'
import { Settings } from '@/routes/Settings'
import { Waiting } from '@/routes/Waiting'

export const App = () => {
  const defaultCalendarParamsPath = toCalendarParamsPath('week', new Date())
  const reporter = useReporter()
  const { user } = useAuth()
  const { pathname } = useLocation()

  if (user?.role === 'court_reporter') {
    return (
      <Sentry.ErrorBoundary
        fallback={({ resetError }) => <Error resetError={resetError} />}
      >
        <CourtReporterRoutes
          defaultCalendarParamsPath={defaultCalendarParamsPath}
        />
      </Sentry.ErrorBoundary>
    )
  }

  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => <Error resetError={resetError} />}
    >
      <MissingReporterSettingsModal
        opened={
          !!reporter?.has_missing_settings && !pathname.startsWith('/settings')
        }
      />

      <Switch>
        <ProtectedRoute role="admin" path="/admin/reporters" exact>
          <Reporters />
        </ProtectedRoute>
        <ProtectedRoute
          role="admin"
          path={`/admin/calendar/${CALENDAR_PARAMS_PATH}`}
          exact
        >
          <AdminDashboard />
        </ProtectedRoute>
        <ProtectedRoute role="admin" path="/admin/calendar" exact>
          <Redirect to={`/admin/calendar/${defaultCalendarParamsPath}`} />
        </ProtectedRoute>
        <ProtectedRoute role="admin" path="/admin/event/:id" exact>
          <AdminEvent />
        </ProtectedRoute>
        <ProtectedRoute
          role="admin"
          path="/admin/court-reporter-event/:id"
          exact
        >
          <AdminCourtReporterEvent />
        </ProtectedRoute>
        <ProtectedRoute role="admin" path="/admin/event/:id/script" exact>
          <AdminScript />
        </ProtectedRoute>
        <ProtectedRoute role="admin" path="/admin" exact>
          <Redirect to="/admin/calendar" />
        </ProtectedRoute>
        <ProtectedRoute role="admin" path="/admin/event/:id/new-script" exact>
          <AdminNewScript />
        </ProtectedRoute>
        <ProtectedRoute
          role="admin"
          path="/admin/event/:id/new-script/state-debug-panel"
        >
          <NewScriptStateEditor />
        </ProtectedRoute>
        <ProtectedRoute role="admin" path="/admin/*">
          <NotFound />
        </ProtectedRoute>
        <ProtectedRoute
          role="reporter"
          path={`/calendar/${CALENDAR_PARAMS_PATH}`}
          exact
        >
          <Dashboard />
        </ProtectedRoute>
        <ProtectedRoute role="reporter" path="/calendar" exact>
          <Redirect to={`/calendar/${defaultCalendarParamsPath}`} />
        </ProtectedRoute>
        <ProtectedRoute role="reporter" path="/event/:id" exact>
          <Event />
        </ProtectedRoute>
        <ProtectedRoute role="reporter" path="/event/:id/script" exact>
          <Script />
        </ProtectedRoute>
        <ProtectedRoute role="reporter" path="/event/:id/new-script" exact>
          <NewScript />
        </ProtectedRoute>
        <ProtectedRoute role="reporter" path="/availability" exact>
          <Availability />
        </ProtectedRoute>
        <ProtectedRoute role="reporter" path="/waiting" exact>
          <Waiting />
        </ProtectedRoute>
        <ProtectedRoute role="reporter" path="/settings" exact>
          <Settings />
        </ProtectedRoute>
        <ProtectedRoute role="reporter" path="/" exact>
          <Redirect to="/calendar" />
        </ProtectedRoute>
        <ProtectedRoute role="reporter" path="*">
          <NotFound />
        </ProtectedRoute>
      </Switch>
    </Sentry.ErrorBoundary>
  )
}

// The DR routes share these paths, so a CR gets a switch of their own.
const CourtReporterRoutes = ({
  defaultCalendarParamsPath,
}: {
  defaultCalendarParamsPath: string
}) => {
  return (
    <Switch>
      <ProtectedRoute
        role="court_reporter"
        path={`/calendar/${CALENDAR_PARAMS_PATH}`}
        exact
      >
        <CourtReporterDashboard />
      </ProtectedRoute>
      <ProtectedRoute role="court_reporter" path="/calendar" exact>
        <Redirect to={`/calendar/${defaultCalendarParamsPath}`} />
      </ProtectedRoute>
      <ProtectedRoute role="court_reporter" path="/event/:id" exact>
        <CourtReporterEvent />
      </ProtectedRoute>
      <ProtectedRoute role="court_reporter" path="/" exact>
        <Redirect to="/calendar" />
      </ProtectedRoute>
      <ProtectedRoute role="court_reporter" path="*">
        <NotFound />
      </ProtectedRoute>
    </Switch>
  )
}

const NotFound = () => (
  <Wall heading="Page Not Found">
    <Button component={Link} to="/">
      Go home
    </Button>
  </Wall>
)

const Error = ({ resetError }: { resetError: () => void }) => (
  <Wall heading="An error has occurred">
    <div className="flex flex-col gap-2">
      <Text size="sm">Please refresh the page</Text>
      <Button onClick={resetError}>Try again</Button>
    </div>
  </Wall>
)
